import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import {
  Heart, X, Loader2, CheckCircle2, XCircle, Sparkles, ArrowRight,
  Volume2, Mic, MicOff,
} from "lucide-react";
import { toast } from "sonner";
import { fireConfettiCannons } from "@/lib/confetti";
import {
  speak, cancelSpeak, createRecognition, normaliseUtterance, similarity,
  SUPPORTS_TTS, SUPPORTS_STT,
} from "@/lib/speech";

/**
 * LanguageLessonModal — Iter81.
 *
 * The exercise player. Pulls 6 LLM-generated exercises from the
 * backend, runs the user through them one by one, tracks hearts +
 * correct/wrong counts, and submits totals at the end.
 *
 * Supported exercise kinds (see backend `routes/language.py`):
 *  - choose-translation: pick 1 of 4 options
 *  - fill-blank:         pick 1 of 4 words for ___
 *  - match-pairs:        tap-to-match target↔native pairs
 *  - build-sentence:     tap word chips to build a target sentence
 *
 * On each wrong answer we show the correct one before advancing, so
 * the user actually learns the material. Lesson result screen gives
 * XP, gems, hearts state, and a "Continue" CTA.
 */
export default function LanguageLessonModal({
  open, onOpenChange, courseId, unitIndex, lessonIndex, onCompleted,
}) {
  const [phase, setPhase] = useState("loading"); // loading | exercise | result | out_of_hearts
  const [exercises, setExercises] = useState([]);
  const [idx, setIdx] = useState(0);
  const [course, setCourse] = useState(null);
  const [feedback, setFeedback] = useState(null); // {correct: bool}
  const [submitting, setSubmitting] = useState(false);
  const [hearts, setHearts] = useState(5);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPhase("loading");
    setIdx(0); setCorrectCount(0); setWrongCount(0); setFeedback(null); setResult(null);
    api.post("/lang/lesson/generate", {
      course_id: courseId, unit_index: unitIndex, lesson_index: lessonIndex,
    })
      .then(({ data }) => {
        setExercises(data?.exercises || []);
        setCourse(data?.course || null);
        setHearts(data?.course?.hearts ?? 5);
        setPhase("exercise");
      })
      .catch((e) => {
        if (e?.response?.status === 429) {
          setPhase("out_of_hearts");
        } else {
          toast.error(e?.response?.data?.detail || "Couldn't load lesson");
          onOpenChange?.(false);
        }
      });
  }, [open, courseId, unitIndex, lessonIndex, onOpenChange]);

  const total = exercises.length;
  const current = exercises[idx];
  const progressPct = total ? (idx / total) * 100 : 0;
  // Iter82 — surface the BCP-47 codes once so every exercise renderer
  // (and the in-exercise TTS / STT controls) can pronounce the right
  // language without re-deriving the mapping.
  const targetLang = course?.target || "en";
  const nativeLang = course?.native || "en";

  // Cancel any in-flight speech when the user changes exercise or
  // closes the modal — prevents the previous prompt from finishing
  // while a new one is on-screen.
  useEffect(() => () => cancelSpeak(), []);
  useEffect(() => { cancelSpeak(); }, [idx, phase, open]);

  const handleAnswer = (correct) => {
    setFeedback({ correct });
    if (correct) {
      setCorrectCount((c) => c + 1);
    } else {
      setWrongCount((w) => w + 1);
      setHearts((h) => Math.max(0, h - 1));
    }
  };

  const advance = async () => {
    setFeedback(null);
    if (idx + 1 < total) {
      setIdx(idx + 1);
      return;
    }
    // End of lesson — submit totals.
    setSubmitting(true);
    try {
      const { data } = await api.post("/lang/lesson/submit", {
        course_id: courseId,
        unit_index: unitIndex,
        lesson_index: lessonIndex,
        wrong_count: wrongCount,
        correct_count: correctCount,
      });
      setResult(data);
      setPhase("result");
      if (data.passed) {
        fireConfettiCannons();
        if (data.xp_global?.leveled_up) {
          window.dispatchEvent(new CustomEvent("oak:level-up", { detail: data.xp_global }));
        }
        window.dispatchEvent(new Event("oak:xp-updated"));
      }
    } catch (e) {
      toast.error("Couldn't save your lesson — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onOpenChange?.(false);
    if (result) onCompleted?.(result);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0B0E14]/95 border-white/10 text-white max-w-xl p-0 overflow-hidden max-h-[90vh] flex flex-col"
        data-testid="lang-lesson-modal"
      >
        {/* Sticky header — hearts + progress + close */}
        <div className="px-5 py-3 border-b border-white/8 flex items-center gap-3 shrink-0">
          <button
            onClick={() => onOpenChange?.(false)}
            className="h-7 w-7 grid place-items-center rounded-md text-white/45 hover:text-white hover:bg-white/5"
            aria-label="Close"
            data-testid="lang-lesson-close"
          >
            <X size={14} />
          </button>
          <div className="flex-1 h-2.5 bg-white/8 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[#00FF9D] to-[#0044FF]"
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.3 }}
              data-testid="lang-lesson-progress"
            />
          </div>
          <div className="flex items-center gap-1 shrink-0" data-testid="lang-lesson-hearts">
            <Heart size={12} className="text-rose-400" fill="currentColor" />
            <span className="text-[12px] font-display tabular-nums text-white/85">{hearts}</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {phase === "loading" && (
            <div className="py-16 grid place-items-center text-white/55 text-sm">
              <Loader2 size={18} className="animate-spin mb-3" />
              <div>Building your lesson…</div>
            </div>
          )}

          {phase === "out_of_hearts" && (
            <OutOfHeartsView course={course} onClose={() => onOpenChange?.(false)} />
          )}

          {phase === "exercise" && current && (
            <ExerciseRenderer
              key={current.i ?? idx}
              exercise={current}
              feedback={feedback}
              onAnswer={handleAnswer}
              targetLang={targetLang}
              nativeLang={nativeLang}
            />
          )}

          {phase === "result" && result && (
            <ResultView result={result} totalExercises={total} onClose={close} />
          )}
        </div>

        {phase === "exercise" && (
          <div className="px-5 py-3 border-t border-white/8 shrink-0">
            {!feedback ? (
              <div className="text-[10.5px] text-white/35 font-mono-oak text-center">
                {idx + 1} of {total} · Tap an answer above
              </div>
            ) : (
              <FeedbackBar feedback={feedback} exercise={current} onContinue={advance} submitting={submitting} />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ====================================================================
// Exercise renderers
// ====================================================================

function ExerciseRenderer({ exercise, feedback, onAnswer, targetLang, nativeLang }) {
  if (exercise.kind === "choose-translation") {
    return <ChooseTranslation exercise={exercise} feedback={feedback} onAnswer={onAnswer} targetLang={targetLang} nativeLang={nativeLang} />;
  }
  if (exercise.kind === "fill-blank") {
    return <FillBlank exercise={exercise} feedback={feedback} onAnswer={onAnswer} targetLang={targetLang} />;
  }
  if (exercise.kind === "match-pairs") {
    return <MatchPairs exercise={exercise} feedback={feedback} onAnswer={onAnswer} targetLang={targetLang} />;
  }
  if (exercise.kind === "build-sentence") {
    return <BuildSentence exercise={exercise} feedback={feedback} onAnswer={onAnswer} targetLang={targetLang} />;
  }
  if (exercise.kind === "listen-repeat") {
    return <ListenRepeat exercise={exercise} feedback={feedback} onAnswer={onAnswer} targetLang={targetLang} />;
  }
  return <div className="p-8 text-white/55 text-sm">Unsupported exercise kind.</div>;
}

// --------------------------------------------------------------------
// Audio helpers — Iter82 TTS speaker pill + STT mic input
// --------------------------------------------------------------------

/**
 * Small icon-only "play pronunciation" button. Renders nothing on
 * browsers without Web Speech support (Firefox on some platforms,
 * older Safari) so we don't tease a broken affordance.
 */
function SpeakerBtn({ text, lang, size = 14, className = "", testId }) {
  if (!SUPPORTS_TTS || !text) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); speak(text, lang); }}
      className={`shrink-0 h-7 w-7 grid place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white/70 hover:text-white hover:bg-white/[0.10] hover:border-white/25 transition ${className}`}
      aria-label="Play pronunciation"
      data-testid={testId || "lang-tts-btn"}
    >
      <Volume2 size={size} />
    </button>
  );
}

/**
 * Microphone button that captures a single utterance in the target
 * language and feeds the normalised transcript back to the caller.
 * The caller decides what "correct" means (string match, fuzzy match,
 * etc) — we just deliver the words.
 */
function MicAnswerBtn({ lang, onTranscript, disabled, testId, label }) {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);

  useEffect(() => () => {
    try { recRef.current?.stop(); } catch (_) { /* noop */ }
  }, []);

  if (!SUPPORTS_STT) return null;

  const start = () => {
    if (disabled || listening) return;
    cancelSpeak();  // don't let TTS bleed into the mic
    const rec = createRecognition(lang);
    if (!rec) return;
    recRef.current = rec;
    setListening(true);
    rec.onresult = (e) => {
      const alts = e.results?.[0] || [];
      // Prefer the first alternative; pass all to caller for fuzzy match.
      const transcripts = [];
      for (let i = 0; i < alts.length; i++) transcripts.push(alts[i].transcript);
      onTranscript?.(transcripts[0] || "", transcripts);
    };
    rec.onerror = (e) => {
      if (e?.error === "not-allowed" || e?.error === "service-not-allowed") {
        toast.error("Mic blocked — allow microphone access in your browser.");
      } else if (e?.error === "no-speech") {
        toast.message("Didn't catch that — try again.");
      }
      setListening(false);
    };
    rec.onend = () => setListening(false);
    try { rec.start(); } catch (_) { setListening(false); }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch (_) { /* noop */ }
    setListening(false);
  };

  return (
    <button
      type="button"
      onClick={listening ? stop : start}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12px] font-display transition ${
        listening
          ? "border-rose-400/55 bg-rose-500/15 text-rose-100 animate-pulse"
          : "border-white/15 bg-white/[0.04] text-white/80 hover:bg-white/[0.08] hover:border-white/30 disabled:opacity-40 disabled:cursor-not-allowed"
      }`}
      data-testid={testId || "lang-mic-btn"}
      aria-label={listening ? "Stop listening" : "Speak your answer"}
    >
      {listening ? <MicOff size={12} /> : <Mic size={12} />}
      {label || (listening ? "Listening…" : "Speak")}
    </button>
  );
}

function ChooseTranslation({ exercise, feedback, onAnswer, targetLang, nativeLang }) {
  const [picked, setPicked] = useState(null);
  const isLocked = feedback !== null;
  const choose = (i) => {
    if (isLocked) return;
    setPicked(i);
    onAnswer(i === exercise.answer);
  };
  // Direction "to-target" → prompt is native, options are in target lang.
  // Direction "to-native" → prompt is target, options are in native lang.
  const promptLang = exercise.direction === "to-target" ? nativeLang : targetLang;
  const optionLang = exercise.direction === "to-target" ? targetLang : nativeLang;
  const headline = exercise.direction === "to-target"
    ? "Translate this sentence"
    : "What does this mean?";

  // Auto-speak the prompt once when the exercise mounts if the prompt
  // is in the target language (the kind the user is learning to hear).
  useEffect(() => {
    if (exercise.direction !== "to-target" && SUPPORTS_TTS) {
      const t = setTimeout(() => speak(exercise.prompt, targetLang), 220);
      return () => clearTimeout(t);
    }
  }, [exercise, targetLang]);

  // Voice answer (only meaningful when the answer is in the target
  // language — i.e. direction === "to-target"). We match the transcript
  // against the 4 options and auto-select whichever the user said.
  const handleTranscript = (utter, alts = []) => {
    if (isLocked) return;
    const all = [utter, ...alts.slice(1)].filter(Boolean).map(normaliseUtterance);
    let bestIdx = -1;
    for (let i = 0; i < exercise.options.length; i++) {
      const norm = normaliseUtterance(exercise.options[i]);
      if (all.some((t) => t === norm || t.includes(norm) || norm.includes(t))) {
        bestIdx = i; break;
      }
    }
    if (bestIdx === -1) {
      toast.message(`Heard: "${utter}" — pick the matching tile.`);
      return;
    }
    choose(bestIdx);
  };

  return (
    <div className="px-6 py-7" data-testid={`lang-ex-choose-${exercise.i}`}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-display mb-2">{headline}</div>
      <div className="flex items-start gap-2 mb-6">
        <div className="text-2xl font-display text-white leading-tight flex-1">{exercise.prompt}</div>
        <SpeakerBtn text={exercise.prompt} lang={promptLang} size={15} testId={`lang-ex-prompt-tts-${exercise.i}`} />
      </div>
      <div className="space-y-2">
        {exercise.options.map((opt, i) => {
          const sel = picked === i;
          const isCorrect = i === exercise.answer;
          const showRight = isLocked && isCorrect;
          const showWrong = isLocked && sel && !isCorrect;
          return (
            <div key={i} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => choose(i)}
                disabled={isLocked}
                className={`flex-1 text-left px-4 py-3 rounded-xl border text-[14px] font-display transition ${
                  showRight
                    ? "border-[#00FF9D]/55 bg-[#00FF9D]/12 text-white"
                    : showWrong
                      ? "border-rose-400/55 bg-rose-500/12 text-white"
                      : sel
                        ? "border-[#0044FF]/55 bg-[#0044FF]/12 text-white"
                        : "border-white/10 bg-white/[0.03] text-white/85 hover:bg-white/[0.06] disabled:cursor-default"
                }`}
                data-testid={`lang-ex-opt-${exercise.i}-${i}`}
              >
                {opt}
              </button>
              {exercise.direction === "to-target" && (
                <SpeakerBtn text={opt} lang={optionLang} size={13} testId={`lang-ex-opt-tts-${exercise.i}-${i}`} />
              )}
            </div>
          );
        })}
      </div>
      {exercise.direction === "to-target" && SUPPORTS_STT && (
        <div className="mt-4 flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border border-white/10 bg-white/[0.025]">
          <div className="text-[11.5px] text-white/55 font-display leading-tight">
            Or speak the answer in {exercise.options[0] ? "your target language" : "target language"}.
          </div>
          <MicAnswerBtn
            lang={targetLang}
            onTranscript={handleTranscript}
            disabled={isLocked}
            testId={`lang-ex-mic-${exercise.i}`}
          />
        </div>
      )}
    </div>
  );
}

function FillBlank({ exercise, feedback, onAnswer, targetLang }) {
  const [picked, setPicked] = useState(null);
  const isLocked = feedback !== null;
  const choose = (i) => {
    if (isLocked) return;
    setPicked(i);
    onAnswer(i === exercise.answer);
  };
  // Auto-pronounce the (un-blanked) sentence once on mount so the
  // learner hears the target language rhythm before guessing.
  useEffect(() => {
    const spoken = exercise.prompt.replace(/_+/g, exercise.options[exercise.answer] || "");
    if (spoken && SUPPORTS_TTS) {
      const t = setTimeout(() => speak(spoken, targetLang), 220);
      return () => clearTimeout(t);
    }
  }, [exercise, targetLang]);
  return (
    <div className="px-6 py-7" data-testid={`lang-ex-fill-${exercise.i}`}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-display mb-2">Fill the blank</div>
      <div className="flex items-start gap-2 mb-2">
        <div className="text-2xl font-display text-white leading-tight flex-1">
          {exercise.prompt.split("___").map((part, i, arr) => (
            <span key={i}>
              {part}
              {i < arr.length - 1 && (
                <span className="inline-block min-w-[2.5em] px-3 mx-1 text-center border-b-2 border-dashed border-white/30 text-white/55">
                  {isLocked ? exercise.options[exercise.answer] : "···"}
                </span>
              )}
            </span>
          ))}
        </div>
        <SpeakerBtn
          text={exercise.prompt.replace(/_+/g, exercise.options[exercise.answer] || "")}
          lang={targetLang}
          size={15}
          testId={`lang-ex-fill-tts-${exercise.i}`}
        />
      </div>
      {exercise.hint_native && (
        <div className="text-[12px] text-white/45 italic mb-5">{exercise.hint_native}</div>
      )}
      <div className="grid grid-cols-2 gap-2">
        {exercise.options.map((opt, i) => {
          const sel = picked === i;
          const isCorrect = i === exercise.answer;
          const showRight = isLocked && isCorrect;
          const showWrong = isLocked && sel && !isCorrect;
          return (
            <div key={i} className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => choose(i)}
                disabled={isLocked}
                className={`flex-1 px-4 py-3 rounded-xl border text-[14px] font-display transition ${
                  showRight
                    ? "border-[#00FF9D]/55 bg-[#00FF9D]/12 text-white"
                    : showWrong
                      ? "border-rose-400/55 bg-rose-500/12 text-white"
                      : sel
                        ? "border-[#0044FF]/55 bg-[#0044FF]/12 text-white"
                        : "border-white/10 bg-white/[0.03] text-white/85 hover:bg-white/[0.06]"
                }`}
                data-testid={`lang-ex-opt-${exercise.i}-${i}`}
              >
                {opt}
              </button>
              <SpeakerBtn text={opt} lang={targetLang} size={12} className="h-6 w-6" testId={`lang-ex-fill-opt-tts-${exercise.i}-${i}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MatchPairs({ exercise, feedback, onAnswer, targetLang }) {
  // Two columns: shuffled target words on left, shuffled native words on right.
  // User taps one from each column to match. Pair correctness validated on the fly.
  const pairs = exercise.pairs;
  const [leftOrder] = useState(() => shuffle(pairs.map((_, i) => i)));
  const [rightOrder] = useState(() => shuffle(pairs.map((_, i) => i)));
  const [picks, setPicks] = useState({ left: null, right: null });
  const [matched, setMatched] = useState({}); // {pairIndex: true}
  const [wrongFlash, setWrongFlash] = useState(false);
  const wrongCountRef = useRef(0);
  const completedRef = useRef(false);

  useEffect(() => {
    if (Object.keys(matched).length === pairs.length && !completedRef.current) {
      completedRef.current = true;
      onAnswer(wrongCountRef.current === 0);
    }
  }, [matched, pairs.length, onAnswer]);

  const handleClick = (side, pairIdx) => {
    if (matched[pairIdx]) return;
    // Also pronounce the target word when the user taps the target column —
    // double-duty: makes selection and gives audio feedback in one tap.
    if (side === "left") speak(pairs[pairIdx].target, targetLang);
    const nextPicks = { ...picks, [side]: pairIdx };
    setPicks(nextPicks);
    if (nextPicks.left !== null && nextPicks.right !== null) {
      if (nextPicks.left === nextPicks.right) {
        setMatched((m) => ({ ...m, [nextPicks.left]: true }));
        setPicks({ left: null, right: null });
      } else {
        wrongCountRef.current += 1;
        setWrongFlash(true);
        setTimeout(() => { setPicks({ left: null, right: null }); setWrongFlash(false); }, 600);
      }
    }
  };

  return (
    <div className="px-6 py-7" data-testid={`lang-ex-match-${exercise.i}`}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-display mb-2">Match the pairs</div>
      <div className="text-base font-display text-white/85 leading-tight mb-5">
        Tap a word, then its translation. <span className="text-white/45">Target column auto-plays audio.</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          {leftOrder.map((pairIdx) => {
            const ok = matched[pairIdx];
            const sel = picks.left === pairIdx;
            return (
              <div key={pairIdx} className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={ok}
                  onClick={() => handleClick("left", pairIdx)}
                  className={`flex-1 px-3 py-2.5 rounded-xl border text-[14px] font-display transition ${
                    ok ? "border-[#00FF9D]/35 bg-[#00FF9D]/8 text-white/55 line-through cursor-default"
                      : sel ? "border-[#0044FF]/55 bg-[#0044FF]/12 text-white"
                      : wrongFlash ? "border-rose-400/40 bg-rose-500/8"
                      : "border-white/10 bg-white/[0.03] text-white hover:bg-white/[0.06]"
                  }`}
                  data-testid={`lang-ex-match-left-${pairIdx}`}
                >
                  {pairs[pairIdx].target}
                </button>
                <SpeakerBtn text={pairs[pairIdx].target} lang={targetLang} size={12} className="h-6 w-6" testId={`lang-ex-match-tts-${pairIdx}`} />
              </div>
            );
          })}
        </div>
        <div className="space-y-2">
          {rightOrder.map((pairIdx) => {
            const ok = matched[pairIdx];
            const sel = picks.right === pairIdx;
            return (
              <button
                key={pairIdx}
                type="button"
                disabled={ok}
                onClick={() => handleClick("right", pairIdx)}
                className={`w-full px-3 py-2.5 rounded-xl border text-[14px] font-display transition ${
                  ok ? "border-[#00FF9D]/35 bg-[#00FF9D]/8 text-white/55 line-through cursor-default"
                    : sel ? "border-[#0044FF]/55 bg-[#0044FF]/12 text-white"
                    : wrongFlash ? "border-rose-400/40 bg-rose-500/8"
                    : "border-white/10 bg-white/[0.03] text-white hover:bg-white/[0.06]"
                }`}
                data-testid={`lang-ex-match-right-${pairIdx}`}
              >
                {pairs[pairIdx].native}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BuildSentence({ exercise, feedback, onAnswer, targetLang }) {
  const [chipsOrder] = useState(() => shuffle(exercise.chips.map((_, i) => i)));
  const [picked, setPicked] = useState([]); // [chipIdx]
  const isLocked = feedback !== null;
  const answerSentence = useMemo(
    () => (exercise.answer_chips || []).join(" "),
    [exercise],
  );

  const submit = () => {
    if (isLocked) return;
    const built = picked.map((i) => exercise.chips[i]);
    const correct = JSON.stringify(built) === JSON.stringify(exercise.answer_chips);
    onAnswer(correct);
  };

  const togglePick = (i) => {
    if (isLocked) return;
    if (picked.includes(i)) {
      setPicked(picked.filter((p) => p !== i));
    } else {
      // Pronounce the chip as it's added — quick audio confirmation
      // and reinforces the sound of each word for the learner.
      speak(exercise.chips[i], targetLang);
      setPicked([...picked, i]);
    }
  };

  // Voice answer — user speaks the whole target sentence; we try to
  // match it to the answer_chips ordering (loose / normalised).
  const handleTranscript = (utter) => {
    if (isLocked) return;
    const said = normaliseUtterance(utter);
    const expected = normaliseUtterance(answerSentence);
    if (!said) return;
    if (said === expected || said.includes(expected) || expected.includes(said)) {
      // Auto-fill the chips in correct order so the learner sees the win.
      const indexed = exercise.answer_chips.map((target) => {
        return exercise.chips.findIndex((c, idx) => c === target && !picked.includes(idx));
      });
      if (indexed.every((i) => i >= 0)) setPicked(indexed);
      onAnswer(true);
      return;
    }
    toast.message(`Heard: "${utter}" — close, try tapping the chips.`);
  };

  return (
    <div className="px-6 py-7" data-testid={`lang-ex-build-${exercise.i}`}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-display mb-2">Build the sentence</div>
      <div className="text-lg font-display text-white/85 mb-2">{exercise.prompt}</div>

      <div className="min-h-[60px] mb-3 px-3 py-3 rounded-xl border border-dashed border-white/15 bg-white/[0.02] flex flex-wrap items-center gap-1.5">
        {picked.length === 0 ? (
          <div className="text-[11px] text-white/30 italic">Tap chips below in order — or hit the mic to speak.</div>
        ) : (
          <>
            {picked.map((i, j) => (
              <button
                key={`pick-${j}-${i}`}
                type="button"
                onClick={() => togglePick(i)}
                className="px-3 py-1.5 rounded-lg bg-[#0044FF]/15 border border-[#0044FF]/45 text-white text-[13px] font-display"
              >
                {exercise.chips[i]}
              </button>
            ))}
            <SpeakerBtn
              text={picked.map((i) => exercise.chips[i]).join(" ")}
              lang={targetLang}
              size={12}
              className="h-6 w-6 ml-auto"
              testId={`lang-ex-build-tts-${exercise.i}`}
            />
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {chipsOrder.map((i) => {
          const used = picked.includes(i);
          return (
            <button
              key={`chip-${i}`}
              type="button"
              disabled={used || isLocked}
              onClick={() => togglePick(i)}
              className={`px-3 py-1.5 rounded-lg border text-[13px] font-display transition ${
                used ? "border-white/5 bg-white/[0.01] text-white/15 cursor-default"
                  : "border-white/12 bg-white/[0.04] text-white hover:bg-white/[0.08]"
              }`}
              data-testid={`lang-ex-build-chip-${exercise.i}-${i}`}
            >
              {exercise.chips[i]}
            </button>
          );
        })}
      </div>

      {!isLocked && (
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={submit}
            disabled={picked.length < 2}
            className="flex-1 btn-primary py-2.5 rounded-full font-display font-semibold text-sm disabled:opacity-40"
            data-testid={`lang-ex-build-submit-${exercise.i}`}
          >
            Check
          </button>
          {SUPPORTS_STT && (
            <MicAnswerBtn
              lang={targetLang}
              onTranscript={handleTranscript}
              disabled={isLocked}
              label="Speak sentence"
              testId={`lang-ex-build-mic-${exercise.i}`}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ====================================================================
// Listen & Repeat — Iter83 strict pronunciation grader
// ====================================================================

/**
 * ListenRepeat: hear a short target-language sentence, then say it
 * back. We grade with `similarity()` (word-level Levenshtein) against
 * an 80% pass threshold — strict enough to require real attempt, loose
 * enough to forgive accent / browser STT noise.
 *
 * Attempt model:
 *  - up to 2 mic attempts before we count it wrong (so a one-off
 *    misrecognition doesn't burn a heart).
 *  - displayed score updates per attempt; best score is kept.
 */
function ListenRepeat({ exercise, feedback, onAnswer, targetLang }) {
  const PASS_THRESHOLD = 0.80;
  const MAX_ATTEMPTS = 2;
  const [bestScore, setBestScore] = useState(0);
  const [bestTranscript, setBestTranscript] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const isLocked = feedback !== null;

  // Auto-play the target sentence once on mount so the learner hears
  // it before being asked to repeat. They can replay at will via the
  // big speaker pill.
  useEffect(() => {
    if (!SUPPORTS_TTS) return;
    const t = setTimeout(() => speak(exercise.target, targetLang, { rate: 0.88 }), 280);
    return () => clearTimeout(t);
  }, [exercise, targetLang]);

  const handleTranscript = (utter, alts = []) => {
    if (isLocked || submitted) return;
    // Score every alternative the browser gave us, keep the best —
    // STT often slots the right transcription as alt #2 or #3.
    const candidates = [utter, ...alts.slice(1)].filter(Boolean);
    let topScore = 0;
    let topTranscript = utter || "";
    for (const c of candidates) {
      const s = similarity(c, exercise.target);
      if (s > topScore) { topScore = s; topTranscript = c; }
    }
    const newBest = Math.max(bestScore, topScore);
    setBestScore(newBest);
    setBestTranscript(topTranscript);
    const used = attempts + 1;
    setAttempts(used);

    if (newBest >= PASS_THRESHOLD) {
      setSubmitted(true);
      onAnswer(true);
      return;
    }
    if (used >= MAX_ATTEMPTS) {
      setSubmitted(true);
      onAnswer(false);
      return;
    }
    toast.message(`${Math.round(topScore * 100)}% close — try once more.`);
  };

  const giveUp = () => {
    if (isLocked || submitted) return;
    setSubmitted(true);
    onAnswer(false);
  };

  const scorePct = Math.round(bestScore * 100);
  const passed = bestScore >= PASS_THRESHOLD;

  return (
    <div className="px-6 py-7" data-testid={`lang-ex-listen-${exercise.i}`}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-display mb-2">
        Listen &amp; repeat
      </div>
      <div className="text-base font-display text-white/85 leading-tight mb-5">
        Tap the speaker, then say the sentence out loud.
      </div>

      {/* Big audio card — the centrepiece of this exercise */}
      <div className="rounded-2xl border border-white/12 bg-white/[0.03] p-5 mb-4 text-center">
        <button
          type="button"
          onClick={() => speak(exercise.target, targetLang, { rate: 0.88 })}
          disabled={!SUPPORTS_TTS}
          className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-[#0044FF]/15 border border-[#0044FF]/45 text-white hover:bg-[#0044FF]/25 transition disabled:opacity-40"
          aria-label="Play sentence"
          data-testid={`lang-ex-listen-play-${exercise.i}`}
        >
          <Volume2 size={22} />
        </button>
        <div className="mt-3 font-display text-xl text-white">{exercise.target}</div>
        <div className="text-[11.5px] text-white/45 italic mt-1">{exercise.native}</div>
        <button
          type="button"
          onClick={() => speak(exercise.target, targetLang, { rate: 0.70 })}
          disabled={!SUPPORTS_TTS}
          className="mt-3 text-[10.5px] text-white/55 hover:text-white underline-offset-2 hover:underline"
          data-testid={`lang-ex-listen-slow-${exercise.i}`}
        >
          Play slowly
        </button>
      </div>

      {/* Score panel — only shown after the user has attempted */}
      {attempts > 0 && (
        <div
          className={`mb-4 rounded-xl border px-3 py-2.5 ${
            passed
              ? "border-[#00FF9D]/40 bg-[#00FF9D]/10"
              : "border-amber-400/40 bg-amber-500/8"
          }`}
          data-testid={`lang-ex-listen-score-${exercise.i}`}
        >
          <div className="flex items-center justify-between text-[11.5px]">
            <span className="font-mono-oak text-white/55">YOU SAID</span>
            <span className={`font-display tabular-nums ${passed ? "text-[#9bffcc]" : "text-amber-200"}`}>
              {scorePct}%
            </span>
          </div>
          <div className="text-[13px] text-white/85 mt-1 truncate">&ldquo;{bestTranscript || "—"}&rdquo;</div>
          <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
            <motion.div
              className={passed ? "h-full bg-[#00FF9D]" : "h-full bg-amber-400"}
              initial={{ width: 0 }}
              animate={{ width: `${scorePct}%` }}
            />
          </div>
          <div className="text-[10px] font-mono-oak text-white/35 mt-1.5">
            Pass = 80% · Attempt {attempts}/{MAX_ATTEMPTS}
          </div>
        </div>
      )}

      {!SUPPORTS_STT ? (
        <div className="text-[12px] text-white/55 italic">
          Your browser doesn&apos;t support speech recognition — skip and we&apos;ll mark this as practice only.
          <button
            type="button"
            onClick={() => { setSubmitted(true); onAnswer(true); }}
            className="mt-3 w-full btn-primary py-2.5 rounded-full font-display font-semibold text-sm"
            data-testid={`lang-ex-listen-skip-${exercise.i}`}
          >
            Mark as practiced
          </button>
        </div>
      ) : !submitted ? (
        <div className="flex items-center gap-2">
          <MicAnswerBtn
            lang={targetLang}
            onTranscript={handleTranscript}
            disabled={isLocked}
            label={attempts === 0 ? "Tap and speak" : "Try again"}
            testId={`lang-ex-listen-mic-${exercise.i}`}
          />
          <button
            type="button"
            onClick={giveUp}
            className="text-[11.5px] text-white/45 hover:text-white px-3 py-2 rounded-md hover:bg-white/5"
            data-testid={`lang-ex-listen-skip-${exercise.i}`}
          >
            Skip
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ====================================================================

function FeedbackBar({ feedback, exercise, onContinue, submitting }) {
  const correctAnswer = useMemo(() => {
    if (exercise.kind === "choose-translation" || exercise.kind === "fill-blank") {
      return exercise.options[exercise.answer];
    }
    if (exercise.kind === "build-sentence") {
      return (exercise.answer_chips || []).join(" ");
    }
    if (exercise.kind === "listen-repeat") {
      return exercise.target;
    }
    return null;
  }, [exercise]);
  const ok = feedback.correct;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`px-3 py-3 rounded-xl flex items-center gap-3 ${
        ok ? "bg-[#00FF9D]/10 border border-[#00FF9D]/30"
           : "bg-rose-500/8 border border-rose-400/30"
      }`}
      data-testid={`lang-ex-feedback-${ok ? "correct" : "wrong"}`}
    >
      {ok
        ? <CheckCircle2 size={20} className="text-[#9bffcc] shrink-0" />
        : <XCircle size={20} className="text-rose-300 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className={`font-display text-[14px] ${ok ? "text-[#9bffcc]" : "text-rose-200"}`}>
          {ok ? "Nice!" : "Not quite."}
        </div>
        {!ok && correctAnswer && (
          <div className="text-[11.5px] text-white/65 mt-0.5">
            Correct answer: <span className="text-white">{correctAnswer}</span>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onContinue}
        disabled={submitting}
        className="btn-primary px-4 py-2 rounded-full text-[12.5px] font-display font-semibold inline-flex items-center gap-1.5"
        data-testid="lang-ex-continue"
      >
        {submitting ? <Loader2 size={11} className="animate-spin" /> : <>Continue <ArrowRight size={11} /></>}
      </button>
    </motion.div>
  );
}

function OutOfHeartsView({ course, onClose }) {
  return (
    <div className="px-6 py-12 text-center" data-testid="lang-out-of-hearts">
      <div className="h-16 w-16 mx-auto rounded-2xl bg-rose-500/12 border border-rose-400/30 grid place-items-center mb-4">
        <Heart size={26} className="text-rose-300" />
      </div>
      <div className="font-display text-xl text-white mb-1">Out of hearts</div>
      <p className="text-[12.5px] text-white/55 max-w-xs mx-auto">
        Hearts regen 1 every 4 hours. Or refill with {50} gems from your dashboard (you have {course?.gems ?? 0}).
      </p>
      <button
        onClick={onClose}
        className="mt-5 btn-primary px-5 py-2 rounded-full font-display font-semibold text-sm"
      >
        Back to course
      </button>
    </div>
  );
}

function ResultView({ result, totalExercises, onClose }) {
  const acc = totalExercises > 0 ? Math.round(((totalExercises - 0) / totalExercises) * 100) : 0;
  return (
    <div className="px-6 py-10 text-center" data-testid="lang-lesson-result">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.45 }}
        className={`h-20 w-20 mx-auto rounded-2xl grid place-items-center mb-4 border ${
          result.passed
            ? "bg-[#00FF9D]/15 border-[#00FF9D]/40"
            : "bg-amber-500/10 border-amber-400/40"
        }`}
      >
        <Sparkles size={28} className={result.passed ? "text-[#9bffcc]" : "text-amber-200"} />
      </motion.div>
      <div className="font-display text-2xl text-white mb-1">
        {result.passed ? "Lesson complete!" : "Almost there…"}
      </div>
      <p className="text-[12.5px] text-white/55 max-w-sm mx-auto">
        {result.passed
          ? "You aced enough exercises to advance. Keep that streak going."
          : "You ran low on hearts. Take a breather and try this lesson again — practice locks it in."}
      </p>
      <div className="grid grid-cols-3 gap-2 max-w-sm mx-auto mt-5">
        <Stat label="XP earned" value={`+${result.xp_gained}`} accent="#9bffcc" />
        <Stat label="Gems" value={`+${result.gems_gained}`} accent="#bcd1ff" />
        <Stat label="Hearts left" value={result.hearts} accent="#ffb0c5" />
      </div>
      <button
        onClick={onClose}
        className="mt-6 btn-primary px-6 py-2.5 rounded-full font-display font-semibold text-sm inline-flex items-center gap-1.5"
        data-testid="lang-lesson-result-close"
      >
        {result.passed ? "Continue learning" : "Try again later"} <ArrowRight size={12} />
      </button>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-display">{label}</div>
      <div className="font-display text-xl tabular-nums mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
